import { MaturityLevel, MaturityScoreResult } from '@/types/assessment';

/**
 * Convert raw score (0-4) to maturity level using clear range-based thresholds
 * 
 * Maturity levels:
 * - Level 0: Absent (score < 0.5)
 * - Level 1: Initial (0.5 <= score < 1.5)
 * - Level 2: Developing (1.5 <= score < 2.5)
 * - Level 3: Established (2.5 <= score < 3.5)
 * - Level 4: Optimized (score >= 3.5)
 */
export const calculateMaturityLevel = (score: number): MaturityLevel => {
  // Handle invalid inputs
  if (!Number.isFinite(score)) {
    return 0 as MaturityLevel;
  }
  
  // Ensure the score is within valid range (0-4)
  score = Math.min(Math.max(score, 0), 4);
  
  // Use clear thresholds for level determination
  let level: MaturityLevel = 0;
  
  if (score >= 3.5) level = 4;
  else if (score >= 2.5) level = 3;
  else if (score >= 1.5) level = 2;
  else if (score >= 0.5) level = 1;
  else level = 0;
  
  return level;
};

// Get label for maturity level
export const getMaturityLevelLabel = (level: MaturityLevel): string => {
  switch (level) {
    case 0:
      return 'Initial / Aware';
    case 1:
      return 'Emerging / Exploring';
    case 2:
      return 'Developing / Practicing';
    case 3:
      return 'Adopting / Performing';
    case 4:
      return 'Evolving / Leading';
    default:
      return 'Initial / Aware';
  }
};

// Get description for maturity level
export const getMaturityLevelDescription = (level: MaturityLevel): string => {
  switch (level) {
    case 0:
      return 'Agile concepts are absent or unknown; dysfunction or inertia is common.';
    case 1:
      return 'Basic awareness exists; initial efforts are ad hoc and inconsistently applied.';
    case 2:
      return 'Practices are forming; Agile is applied with moderate understanding and growing belief.';
    case 3:
      return 'Agile mindset and practices are consistently applied; collaboration and ownership are evident.';
    case 4:
      return 'Agile principles are deeply embedded; the team is self-improving, resilient, and exemplary.';
    default:
      return 'Agile concepts are absent or unknown; dysfunction or inertia is common.';
  }
};

// Get color for maturity level
export const getMaturityLevelColor = (level: MaturityLevel): string => {
  switch (level) {
    case 0:
      return '#000000'; // Black for Absent
    case 1:
      return '#fbbf24'; // Yellow for Initial
    case 2:
      return '#fb923c'; // Orange for Developing
    case 3:
      return '#ea580c'; // Dark Orange for Established
    case 4:
      return '#22c55e'; // Green for Optimized
    default:
      return '#000000'; // Black default
  }
};

/**
 * Generate a complete maturity score result object from a raw score
 * @param score The raw maturity score (0-4)
 * @returns A MaturityScoreResult object with score, level, and label
 */
export const getMaturityScoreResult = (score: number): MaturityScoreResult => {
  // Handle NaN or undefined values
  if (!Number.isFinite(score)) {
    score = 0;
  }
  
  const level = calculateMaturityLevel(score);
  const result = {
    score,
    level,
    label: getMaturityLevelLabel(level),
    description: getMaturityLevelDescription(level)
  };
  
  return result;
};

// Calculate overall maturity score from all categories
export const calculateOverallMaturity = (
  scoresByCategory: Record<string, {current: number, target: number}>
): { current: MaturityScoreResult, target: MaturityScoreResult } => {
  // If scoresByCategory is empty, try to get from localStorage
  let workingScores = scoresByCategory;
  if (Object.keys(scoresByCategory).length === 0) {
    const savedScores = localStorage.getItem('assessment-results-scores');
    if (savedScores) {
      try {
        workingScores = JSON.parse(savedScores);
      } catch (error) {
        console.error('calculateOverallMaturity: Error parsing saved scores:', error);
      }
    }
  }
  
  // Calculate average scores across all categories
  const scores = Object.values(workingScores);
  
  if (scores.length === 0) {
    return {
      current: getMaturityScoreResult(0),
      target: getMaturityScoreResult(0)
    };
  }
  
  // Filter out any invalid scores
  const validScores = scores.filter(score => 
    Number.isFinite(score.current) && Number.isFinite(score.target)
  );
  
  // If no valid scores, return default
  if (validScores.length === 0) {
    return {
      current: getMaturityScoreResult(0),
      target: getMaturityScoreResult(0)
    };
  }
  
  // If we have only one valid score, use it directly
  if (validScores.length === 1) {
    return {
      current: getMaturityScoreResult(validScores[0].current),
      target: getMaturityScoreResult(validScores[0].target)
    };
  }
  
  const currentTotal = validScores.reduce((sum, score) => sum + score.current, 0);
  const targetTotal = validScores.reduce((sum, score) => sum + score.target, 0);
  
  // Round to 1 decimal place
  const currentAvg = Math.round((currentTotal / validScores.length) * 10) / 10;
  const targetAvg = Math.round((targetTotal / validScores.length) * 10) / 10;
  
  const current = getMaturityScoreResult(currentAvg);
  const target = getMaturityScoreResult(targetAvg);
  
  return { current, target };
};
